/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AddEditHeaderComponent } from './addEditHeader/addEditHeader.component';
import { BaseComponent } from '../../../../../../../core/components';
import { Component, ComponentRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { L7HeaderModel } from '../../../../models/l7.header.model';
import { Layer7ApiService } from '../../../../http';
import { LogService, UtilsService } from '../../../../../../../core/services';
import { WizardService } from '../../../../services';
import { WizardTitleEnum } from '../../../../enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-l7headers',
    templateUrl: './l7Headers.component.html',
    styleUrls: ['./l7Headers.component.scss']
})

export class L7HeadersComponent extends BaseComponent implements OnInit {
    /**
     * Add/Edit page for headers.
     */
    @ViewChild('addEditContainer', { read: ViewContainerRef, static: true })
    public addEditContainer: { createComponent: (arg0: typeof AddEditHeaderComponent) => any; };

    /**
     * Service Id from parent component to make API call.
     */
    @Input() public serviceId: string;

    /**
     * Template index for storing the headers in the controller state.
     */
    @Input() public templateIndex: number;

    /**
     * Template name for storing the headers in the controller state.
     */
    @Input() public templateName: WizardTitleEnum;

    /**
     * Header out model.
     */
    @Output() public headerIdsOut: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Table row data for display in the view.
     */
    public tableRows: any[] = [];

    /**
     * Boolean for showing and hiding this component when in add / edit mode.
     */
    public addEditMode: boolean = false;

    /**
     * Headers output object.
     */
    public headersOutput = {
        add: [],
        update: [],
        delete: []
    };

    /**
     * Component Reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    /**
     * When adding or editing to store the header's data.
     * @private
     */
    private _headers: L7HeaderModel[] = [];

    constructor(
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private utilsService: UtilsService,
        private wizardService: WizardService
    ) {
        super();
        this.utilsService.createArrayIfNotExist(this.wizardService.controllerState, WizardTitleEnum.HEADERS);
        this.utilsService.createArrayIfNotExist(
            this.wizardService.controllerState, WizardTitleEnum.HEADERS[this.templateIndex + this.templateName + '-order']
        );
    }

    set headers(headers: L7HeaderModel[]) {
        this._headers = headers;
    }

    get headers(): L7HeaderModel[] {
        return this._headers;
    }

    ngOnInit(): void {
        this.setUpPageData();
    }

    /**
     * Header order emitted to parent service.
     * @param $event
     */
    public emitHeaderIdOrder($event: any): void {
        this.headerIdsOut.next($event);
        this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateIndex + this.templateName + '-order'] = $event;
    }

    /**
     * Create the add / edit component.
     * @param id
     */
    public createAddEditComponent(id?: string): void {
        const addEditComponent: any = this.addEditContainer.createComponent(AddEditHeaderComponent);
        addEditComponent.instance.header = id ? this.headers.find((item: L7HeaderModel): boolean => item.id === id) : null;
        addEditComponent.instance.headers = this.headers;
        addEditComponent.instance.callBack = (header: L7HeaderModel): void => {
            this.addEditHeaderCallback(header);
        };
        this.componentReference = addEditComponent;
        this.addEditMode = true;
        this.wizardService.disableNavigation = true;
    }

    /**
     * cancel Add / Edit header call back.
     * @param header
     * @private
     */
    private addEditHeaderCallback(header: L7HeaderModel): void {
        if (header) {
            if (header.id.includes('temp-')) {
                this.updateAddOutput(header);
            } else {
                this.updateUpdateOutput(header);
            }

            this.updateHeaders(header);
            this.updateStates();
            this.populateTablesRows();
        }

        this.addEditMode = false;
        this.componentReference.destroy();
        this.wizardService.disableNavigation = false;
    }

    /**
     * Delete header from stored states.
     * @param id
     */
    public deleteHeader(id: string): void {
        if (id.includes('temp-')) {
            const deleteAddIndex: number = this.headersOutput.add.findIndex((item: any): boolean => item.id === id);
            this.headersOutput.add.splice(deleteAddIndex, 1);
        } else {
            this.headersOutput.delete.push(id);
        }

        const deleteIndex: number = this.headers.findIndex((item: L7HeaderModel): boolean => item.id === id);
        if (deleteIndex !== -1) {
            this.headers.splice(deleteIndex, 1);
            this.populateTablesRows();
        }

        // eslint-disable-next-line max-len
        const controllerOrder: any = this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateIndex + this.templateName + '-order'];
        if (controllerOrder) {
            const orderedDeleteIndex = controllerOrder.indexOf(id);
            if (orderedDeleteIndex !== -1) {
                controllerOrder.splice(deleteIndex, 1);
                this.headerIdsOut.next(controllerOrder);
            }
        }

        this.updateStates();
    }

    /**
     * Update the headers output add object.
     * @param header
     * @private
     */
    private updateAddOutput(header: L7HeaderModel): void {
        const addIndex: number = this.headersOutput.add.findIndex((item: any): boolean => item.id === header.id);
        if (addIndex !== -1) {
            this.headersOutput.add[addIndex] = header;
        } else {
            this.headersOutput.add.push(header);
        }
    }

    /**
     * Update the headers output update object.
     * @param header
     * @private
     */
    private updateUpdateOutput(header: L7HeaderModel): void {
        const updateIndex: number = this.headersOutput.update.findIndex((item: any): boolean => item.id === header.id);
        if (updateIndex !== -1) {
            this.headersOutput.update[updateIndex] = header;
        } else {
            this.headersOutput.update.push(header);
        }
    }

    /**
     * Update the headers object.
     * @param header
     * @private
     */
    private updateHeaders(header: L7HeaderModel): void {
        const headerIndex: number = this.headers.findIndex((item: L7HeaderModel): boolean => item.id === header.id);
        if (headerIndex !== -1) {
            this.headers[headerIndex] = header;
        } else {
            this.headers.push(header);
        }
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        if (this.serviceId && this.utilsService.isServiceId(this.serviceId) && !this.serviceId.includes('$')) {
            this.layer7ApiService.getHeaderConfig(this.serviceId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (headers: L7HeaderModel[]): void => {
                        // eslint-disable-next-line max-len
                        this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateIndex + this.templateName + '-order'] = headers;
                        this.headers = headers;
                        this.updatePageVariables();
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('L7HeadersComponent:setUpPageData', error.message);
                    }
                });
        } else {
            const headers = this.utilsService.getObjectValue(
                this.wizardService.controllerState[WizardTitleEnum.HEADERS],
                `${this.templateName + this.templateIndex}`
            );

            const order = this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateIndex + this.templateName + '-order'];
            const formattedHeaders: L7HeaderModel[] = this.formatControllerStateHeaders(headers);
            this.headers = this.utilsService.sortArrayByProperty(formattedHeaders, 'id', order);
            // If the header is in the controller state it is new and should be added to the headersOutput add array
            this.populateHeaderOutput(formattedHeaders);
            this.updatePageVariables();
        }
    }

    /**
     * Format controller state.
     * @param headers
     * @private
     */
    private formatControllerStateHeaders(headers: any): L7HeaderModel[] {
        let formattedHeaders: any[] = [];
        for (const property in headers) {
            if (headers.hasOwnProperty(property)) {
                formattedHeaders = formattedHeaders.concat(headers[property]);
            }
        }

        return formattedHeaders;
    }

    /**
     * Populate header output.
     * @param formattedHeaders
     * @private
     */
    private populateHeaderOutput(formattedHeaders: any[]): void {
        for (const header of formattedHeaders) {
            const add: any[] = this.headersOutput.add;
            if (!add.includes(header)) {
                add.push(header);
            }
        }

        this.headerIdsOut.next(
            this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateIndex + this.templateName + '-order']
        );
    }

    /**
     * Update page variables
     * @private
     */
    private updatePageVariables(): void {
        const ids: string[] = this.headers.map((item: L7HeaderModel): string => item.id);
        this.headerIdsOut.next(ids);
        this.populateTablesRows();
    }

    /**
     * Populate the table rows from the currentHeaders data.
     * @private
     */
    private populateTablesRows(): void {
        this.tableRows = [];
        if (this.headers && this.headers.length) {
            for (const header of this.headers) {
                this.tableRows.push({
                    id: header.id,
                    type: header.type,
                    option: header.option,
                    name: header.name,
                    value: header.value ? header.value : '-',
                    replace: header.replace ? header.replace : '-'
                });
            }
        }
    }

    /**
     * Format the output for parent components.
     * @private
     */
    private updateStates(): void {
        const orderedIds: string[] = this.headers.map(item => item.id);
        this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateName + this.templateIndex] =
            this.utilsService.duplicateObject(this.headersOutput);
        this.wizardService.controllerState[WizardTitleEnum.HEADERS][this.templateIndex + this.templateName + '-order'] = orderedIds;
        this.headerIdsOut.next(orderedIds);
    }
}
